import {
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { useStoreSelector } from "../../../../src/lib/store";
import {
  closeBindingEditor,
  controlSessionStore,
  patchBindingEditorDraft,
} from "../controls/controlSession";
import {
  controlBindingSpecKey,
  suggestRelativeOutputRange,
  type ControlBindingCurve,
  type ControlBindingMode,
  type ControlBindingSpec,
} from "../controls/controlBindings";
import { resolveParamAddress } from "../controls/paramAddresses";

const STORAGE_KEY = "audio-lab:binding-editor-modal";
const DEFAULT_WIDTH = 400;
const DEFAULT_HEIGHT = 420;
const MIN_WIDTH = 340;
const MIN_HEIGHT = 300;

const MODES: ControlBindingMode[] = [
  "absolute",
  "relative",
  "trigger",
  "toggle",
  "step",
];
const CURVES: ControlBindingCurve[] = ["linear", "exp", "log", "smoothstep"];

type PanelLayout = { left: number; top: number; width: number; height: number };

function clampSize(width: number, height: number): { width: number; height: number } {
  const maxW = Math.max(MIN_WIDTH, window.innerWidth - 16);
  const maxH = Math.max(MIN_HEIGHT, window.innerHeight - 16);
  return {
    width: Math.min(maxW, Math.max(MIN_WIDTH, Math.round(width))),
    height: Math.min(maxH, Math.max(MIN_HEIGHT, Math.round(height))),
  };
}

function clampPos(
  pos: { left: number; top: number },
  width: number,
  height: number,
): { left: number; top: number } {
  const maxLeft = Math.max(8, window.innerWidth - width - 8);
  const maxTop = Math.max(8, window.innerHeight - height - 8);
  return {
    left: Math.min(maxLeft, Math.max(8, pos.left)),
    top: Math.min(maxTop, Math.max(8, pos.top)),
  };
}

function defaultLayout(): PanelLayout {
  const size = clampSize(DEFAULT_WIDTH, DEFAULT_HEIGHT);
  return {
    ...clampPos(
      {
        left: Math.max(12, window.innerWidth - size.width - 24),
        top: Math.max(12, Math.min(72, window.innerHeight * 0.1)),
      },
      size.width,
      size.height,
    ),
    ...size,
  };
}

function loadLayout(): PanelLayout {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return defaultLayout();
    }
    const parsed = JSON.parse(raw) as Partial<PanelLayout>;
    const size = clampSize(
      typeof parsed.width === "number" ? parsed.width : DEFAULT_WIDTH,
      typeof parsed.height === "number" ? parsed.height : DEFAULT_HEIGHT,
    );
    const left = typeof parsed.left === "number" ? parsed.left : defaultLayout().left;
    const top = typeof parsed.top === "number" ? parsed.top : defaultLayout().top;
    return { ...clampPos({ left, top }, size.width, size.height), ...size };
  } catch {
    return defaultLayout();
  }
}

function saveLayout(layout: PanelLayout): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(layout));
  } catch {
    // ignore
  }
}

/**
 * Floating mapping editor — same chrome as Settings (drag, no modal backdrop).
 */
export function BindingEditorModal() {
  const editor = useStoreSelector(controlSessionStore, s => s.bindingEditor);
  const panelRef = useRef<HTMLDivElement>(null);
  const dragCleanupRef = useRef<(() => void) | undefined>(undefined);
  const [layout, setLayout] = useState<PanelLayout>(() => loadLayout());
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    if (!editor) {
      return;
    }
    function onKey(event: KeyboardEvent): void {
      if (event.key !== "Escape") {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      closeBindingEditor();
    }
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [editor]);

  function beginDrag(event: ReactPointerEvent): void {
    if (event.button !== 0) {
      return;
    }
    if ((event.target as HTMLElement | null)?.closest("button, input, select, label")) {
      return;
    }
    event.preventDefault();
    const el = panelRef.current;
    if (!el) {
      return;
    }
    dragCleanupRef.current?.();
    const rect = el.getBoundingClientRect();
    const offsetX = event.clientX - rect.left;
    const offsetY = event.clientY - rect.top;
    setDragging(true);
    const onMove = (moveEvent: PointerEvent): void => {
      setLayout(prev => {
        const pos = clampPos(
          { left: moveEvent.clientX - offsetX, top: moveEvent.clientY - offsetY },
          prev.width,
          prev.height,
        );
        const next = { ...prev, ...pos };
        saveLayout(next);
        return next;
      });
    };
    const onUp = (): void => {
      setDragging(false);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      dragCleanupRef.current = undefined;
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    dragCleanupRef.current = onUp;
  }

  if (!editor) {
    return null;
  }

  const draft = editor.draft;
  const inputRange = draft.inputRange ?? [0, 1];
  const outputRange = draft.outputRange ?? [0, 1];
  const selectedFieldKey = controlBindingSpecKey({
    path: draft.path,
    selector: draft.selector,
  });

  function setDraft(updater: (current: ControlBindingSpec) => ControlBindingSpec): void {
    patchBindingEditorDraft(updater);
  }

  return createPortal(
    <div
      ref={panelRef}
      className={`help-modal binding-editor-modal fixed z-[60] flex flex-col overflow-hidden rounded-lg border border-lab-line bg-lab-panel text-[12px] text-lab-text shadow-[0_24px_48px_rgba(0,0,0,0.45)]${
        dragging ? " select-none" : ""
      }`}
      role="dialog"
      aria-label="Binding"
      style={{
        left: layout.left,
        top: layout.top,
        width: layout.width,
        height: layout.height,
      }}
    >
      <div
        className="help-modal-header flex shrink-0 cursor-move touch-none select-none items-center justify-between gap-2 border-b border-lab-line bg-lab-panel-bar px-[0.65rem] py-1.5"
        onPointerDown={beginDrag}
      >
        <span className="help-modal-title text-[12px] font-semibold">
          Binding
        </span>
        <button
          type="button"
          className="help-modal-close rounded border border-transparent bg-transparent px-2 py-[0.2rem] text-[12px] text-lab-muted hover:border-lab-line hover:bg-white/[0.04] hover:text-lab-text"
          onPointerDown={event => event.stopPropagation()}
          onClick={() => closeBindingEditor()}
        >
          Close
        </button>
      </div>

      <div className="min-h-0 flex-1 space-y-3 overflow-auto px-3 py-3">
        <p className="m-0 text-[11px] text-lab-muted">
          Target: <span className="text-slate-300">{editor.targetLabel}</span>
          <span className="font-mono text-[10px] text-slate-500"> ({editor.target})</span>
        </p>

        <div className="grid grid-cols-2 gap-2 text-[11px]">
          <Field label="Source">
            <input
              className={inputClass}
              value={draft.path}
              onChange={event => {
                const nextPath = event.target.value;
                setDraft(current => ({ ...current, path: nextPath }));
              }}
            />
          </Field>
          <Field label="Field">
            <select
              className={inputClass}
              value={selectedFieldKey}
              onChange={event => {
                const key = event.target.value;
                const next = editor.fieldOptions.find(
                  option =>
                    controlBindingSpecKey({
                      path: draft.path,
                      selector: option.selector,
                    }) === key,
                );
                if (!next) {
                  return;
                }
                setDraft(current => ({
                  ...current,
                  selector: next.selector,
                  inputRange: next.suggestedRange ?? current.inputRange,
                }));
              }}
            >
              {editor.fieldOptions.length === 0 ? (
                <option value="">(default)</option>
              ) : (
                editor.fieldOptions.map(option => {
                  const key = controlBindingSpecKey({
                    path: draft.path,
                    selector: option.selector,
                  });
                  return (
                    <option key={key} value={key}>
                      {option.label}
                    </option>
                  );
                })
              )}
            </select>
          </Field>
          <Field label="Input min/max">
            <div className="flex gap-1">
              <input
                type="number"
                className={inputClass}
                value={inputRange[0]}
                onChange={event => {
                  const value = Number(event.target.value);
                  if (!Number.isFinite(value)) {
                    return;
                  }
                  setDraft(current => ({
                    ...current,
                    inputRange: [value, (current.inputRange ?? inputRange)[1]],
                  }));
                }}
              />
              <input
                type="number"
                className={inputClass}
                value={inputRange[1]}
                onChange={event => {
                  const value = Number(event.target.value);
                  if (!Number.isFinite(value)) {
                    return;
                  }
                  setDraft(current => ({
                    ...current,
                    inputRange: [(current.inputRange ?? inputRange)[0], value],
                  }));
                }}
              />
            </div>
          </Field>
          <Field label="Output min/max">
            <div className="flex gap-1">
              <input
                type="number"
                className={inputClass}
                value={outputRange[0]}
                onChange={event => {
                  const value = Number(event.target.value);
                  if (!Number.isFinite(value)) {
                    return;
                  }
                  setDraft(current => ({
                    ...current,
                    outputRange: [value, (current.outputRange ?? outputRange)[1]],
                  }));
                }}
              />
              <input
                type="number"
                className={inputClass}
                value={outputRange[1]}
                onChange={event => {
                  const value = Number(event.target.value);
                  if (!Number.isFinite(value)) {
                    return;
                  }
                  setDraft(current => ({
                    ...current,
                    outputRange: [(current.outputRange ?? outputRange)[0], value],
                  }));
                }}
              />
            </div>
          </Field>
          <Field label="Mode">
            <select
              className={inputClass}
              value={draft.mode ?? "absolute"}
              onChange={event => {
                const mode = event.target.value as ControlBindingMode;
                setDraft(current => {
                  if (mode !== "relative") {
                    return { ...current, mode };
                  }
                  const paramRange =
                    resolveParamAddress(editor.target)?.range ?? outputRange;
                  return {
                    ...current,
                    mode,
                    outputRange: suggestRelativeOutputRange(
                      current.outputRange ?? outputRange,
                      paramRange,
                    ),
                  };
                });
              }}
            >
              {MODES.map(mode => (
                <option key={mode} value={mode}>
                  {mode}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Curve">
            <select
              className={inputClass}
              value={draft.curve ?? "linear"}
              onChange={event => {
                const curve = event.target.value as ControlBindingCurve;
                setDraft(current => ({ ...current, curve }));
              }}
            >
              {CURVES.map(curve => (
                <option key={curve} value={curve}>
                  {curve}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <div className="flex items-center gap-4 text-[11px] text-lab-muted">
          <label className="inline-flex items-center gap-1.5">
            <input
              type="checkbox"
              checked={draft.clamp ?? true}
              onChange={event =>
                setDraft(current => ({ ...current, clamp: event.target.checked }))
              }
            />
            Clamp
          </label>
          <label className="inline-flex items-center gap-1.5">
            <input
              type="checkbox"
              checked={draft.invert ?? false}
              onChange={event =>
                setDraft(current => ({ ...current, invert: event.target.checked }))
              }
            />
            Invert
          </label>
        </div>
      </div>
    </div>,
    document.body,
  );
}

const inputClass =
  "h-7 w-full rounded-md border border-lab-line bg-lab-panel-deep px-2 text-[11px] text-lab-text outline-none focus:border-amber-400/50";

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-[0.14em] text-lab-muted">
        {label}
      </span>
      {children}
    </label>
  );
}
